// watch/SaveMenu.tsx — the watch page's Save (1.27.50, owner concept: "Add to Watch Later or
// choose a collection"). Watch later first, then the member's own Bookmarks folders, then a
// new folder. A saved video shows which folder it is in and can be moved or removed.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, Popover, useToast } from '@/ui';
import { Icon } from '@/icons';
import { usePostSave, useRequireAuth, useSession, WATCH_LATER_FOLDER_ID } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import type { Post } from '@/core/models/post';
import { ActionPill, MenuItem, MenuPanel, RoundAction } from './actionStyles';

const NameForm = styled.form`
  display: flex;
  gap: 6px;
  padding: 4px 12px 8px;
  input {
    flex: 1;
    min-width: 0;
    padding: 6px 8px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  }
`;

/** `round` — Flash's action column style (a round button over its label). */
export function SaveMenu({ post, round = false }: { post: Post; round?: boolean }) {
  const { isAuthenticated } = useSession();
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const toast = useToast();
  const save = usePostSave(post, Boolean(isAuthenticated));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  async function run(label: string, work: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await work();
      toast(done);
      setOpen(false);
      setNaming(false);
      setName('');
    } catch {
      toast(label);
    } finally {
      setBusy(false);
    }
  }

  const label = save.bookmark ? 'Saved' : 'Save';
  const Trigger = round ? RoundAction : ActionPill;
  const face = round ? (
    <>
      <span>
        <Icon name="bookmark" size={22} />
      </span>
      <span>{label}</span>
    </>
  ) : (
    <>
      <Icon name="bookmark" size={18} />
      {label}
    </>
  );

  if (!isAuthenticated) {
    return (
      <Trigger type="button" onClick={() => requireAuth('save videos')}>
        {face}
      </Trigger>
    );
  }
  // 1.27.70: on the Videos pages, saving needs two-factor sign-in; the button brings up the reminder.
  if (gate.locked) {
    return (
      <Trigger type="button" onClick={() => gate.allow('save videos')}>
        {face}
      </Trigger>
    );
  }

  const trigger = (
    <Trigger
      type="button"
      $on={Boolean(save.bookmark)}
      aria-label={save.bookmark ? `Saved to ${save.savedIn}` : 'Save'}
    >
      {face}
    </Trigger>
  );

  const radio = (folderId: string, label: string, icon: 'clock' | 'folder') => {
    const on = save.bookmark?.folderId === folderId;
    return (
      <MenuItem
        key={folderId}
        type="button"
        role="menuitemradio"
        aria-checked={on}
        disabled={busy}
        onClick={() =>
          void run("Couldn't save the video", () => save.saveTo(folderId), `Saved to ${label}`)
        }
      >
        <Icon name={icon} size={16} />
        <span>{label}</span>
        {on && <Icon name="check" size={16} />}
      </MenuItem>
    );
  };

  return (
    <Popover trigger={trigger} open={open} onOpenChange={setOpen} align="start">
      <MenuPanel role="menu" aria-label="Save to">
        <h3>Save to</h3>
        {radio(WATCH_LATER_FOLDER_ID, 'Watch later', 'clock')}
        {save.folders.map((f) => radio(f.id, f.name, 'folder'))}
        <hr />
        {naming ? (
          <NameForm
            onSubmit={(e) => {
              e.preventDefault();
              const clean = name.trim().slice(0, 60);
              if (!clean) return;
              void run(
                "Couldn't create the folder",
                () => save.createFolderAndSave(clean),
                `Saved to ${clean}`,
              );
            }}
          >
            <input
              aria-label="New folder name"
              placeholder="Folder name"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              ref={(el) => el?.focus()}
            />
            <Button type="submit" size="sm" disabled={busy || !name.trim()}>
              Create
            </Button>
          </NameForm>
        ) : (
          <MenuItem type="button" disabled={busy} onClick={() => setNaming(true)}>
            <Icon name="folder-plus" size={16} />
            <span>New folder…</span>
          </MenuItem>
        )}
        {save.bookmark && (
          <MenuItem
            type="button"
            data-danger="true"
            disabled={busy}
            onClick={() =>
              void run("Couldn't remove it", () => save.remove(), `Removed from ${save.savedIn}`)
            }
          >
            <Icon name="trash" size={16} />
            <span>Remove from {save.savedIn}</span>
          </MenuItem>
        )}
      </MenuPanel>
    </Popover>
  );
}
